import React, { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Switch } from '@/components/ui/switch';
import { Plus, Trash2, GripVertical } from 'lucide-react';
import { EnumAlertStatus } from '@/lib/api/types.gen';

interface StepConfig {
  key: string;
  title: string;
  instructions: string;
  hasNote: boolean;
  hasCheckbox: boolean;
  stepNumber?: number;
}

interface DispositionWorkflowBuilderProps {
  steps: StepConfig[];
  status: EnumAlertStatus;
  onChange: (steps: StepConfig[], status?: EnumAlertStatus) => void;
  readOnly?: boolean;
}

function DispositionWorkflowBuilder({
  steps,
  status,
  onChange,
  readOnly = false,
}: DispositionWorkflowBuilderProps) {
  const [localSteps, setLocalSteps] = useState<StepConfig[]>(steps);
  const [draggedItem, setDraggedItem] = useState<number | null>(null);
  const [dragOverItem, setDragOverItem] = useState<number | null>(null);

  // Update local steps when the steps prop changes
  React.useEffect(() => {
    setLocalSteps(steps);
  }, [steps]);

  function handleStepChange(idx: number, field: keyof StepConfig, value: string | boolean) {
    // Type guard for value
    let typedValue: string | boolean = value as string | boolean;
    if (typeof localSteps[idx][field] === 'boolean') {
      typedValue = Boolean(value);
    } else if (typeof localSteps[idx][field] === 'string') {
      typedValue = String(value);
    }
    const updated = localSteps.map((s: StepConfig, i: number) =>
      i === idx ? { ...s, [field]: typedValue } : s
    );
    setLocalSteps(updated);
    onChange(updated);
  }

  function handleAddStep() {
    const newStep: StepConfig = {
      key: `step-${Date.now()}`,
      title: '',
      instructions: '',
      hasNote: false,
      hasCheckbox: false,
    };
    setLocalSteps([...localSteps, newStep]);
    onChange([...localSteps, newStep]);
  }

  function handleRemoveStep(idx: number) {
    const updated = localSteps.filter((_: StepConfig, i: number) => i !== idx);
    setLocalSteps(updated);
    onChange(updated);
  }

  // Handle drag start
  function handleDragStart(e: React.DragEvent<HTMLDivElement>, idx: number) {
    setDraggedItem(idx);
    e.dataTransfer.effectAllowed = 'move';
    // Add a ghost image
    const draggedElement = e.currentTarget;
    if (draggedElement) {
      e.dataTransfer.setDragImage(draggedElement, 20, 20);
    }
  }

  // Handle drag over
  function handleDragOver(e: React.DragEvent<HTMLDivElement>, idx: number) {
    e.preventDefault();
    setDragOverItem(idx);
    e.dataTransfer.dropEffect = 'move';
  }

  // Handle drop
  function handleDrop(e: React.DragEvent<HTMLDivElement>) {
    e.preventDefault();

    if (draggedItem === null || dragOverItem === null || draggedItem === dragOverItem) {
      return;
    }

    // Create a copy of the steps array
    const updatedSteps = [...localSteps];

    // Remove the dragged item
    const draggedStep = updatedSteps[draggedItem];
    updatedSteps.splice(draggedItem, 1);

    // Insert at the new position
    updatedSteps.splice(dragOverItem, 0, draggedStep);

    // Update step numbers if needed
    const finalSteps = updatedSteps.map((step, index) => ({
      ...step,
      stepNumber: index + 1,
    }));

    // Update state and notify parent
    setLocalSteps(finalSteps);
    onChange(finalSteps);

    // Reset drag state
    setDraggedItem(null);
    setDragOverItem(null);
  }

  // Handle drag end
  function handleDragEnd() {
    setDraggedItem(null);
    setDragOverItem(null);
  }

  return (
    <div className="space-y-6">
      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <h3 className="text-lg font-semibold text-white">
            Steps for {status.replace(/([A-Z])/g, ' $1').trim()}
          </h3>
          {!readOnly && (
            <div className="flex items-center gap-4">
              <Button type="button" variant="outline" onClick={handleAddStep} disabled={readOnly}>
                <Plus className="mr-1 h-4 w-4" /> Add Step
              </Button>
            </div>
          )}
        </div>
      </div>
      <div className="space-y-4">
        {localSteps.length === 0 && (
          <div className="text-gray-400">No steps defined. Add your first step.</div>
        )}
        {localSteps.map((step, idx) => (
          <div
            key={step.key}
            className={`flex flex-col gap-3 rounded-lg border border-gray-700 bg-[#111] p-4 ${dragOverItem === idx ? 'border-2 border-blue-500' : ''} ${draggedItem === idx ? 'opacity-50' : ''}`}
            draggable={!readOnly}
            onDragStart={(e) => handleDragStart(e, idx)}
            onDragOver={(e) => handleDragOver(e, idx)}
            onDrop={handleDrop}
            onDragEnd={handleDragEnd}
          >
            <div className="flex items-center gap-4">
              {!readOnly && (
                <div className="cursor-grab">
                  <GripVertical className="h-5 w-5 text-gray-500" />
                </div>
              )}
              <Input
                className="bg-[#222] text-white"
                placeholder="Step Title"
                value={step.title}
                onChange={(e) => handleStepChange(idx, 'title', e.target.value)}
                readOnly={readOnly}
                disabled={readOnly}
              />
              {!readOnly && (
                <Button type="button" variant="ghost" onClick={() => handleRemoveStep(idx)}>
                  <Trash2 className="h-4 w-4 text-red-500" />
                </Button>
              )}
            </div>
            <Textarea
              className="bg-[#222] text-white"
              placeholder="Step Instructions"
              value={step.instructions}
              onChange={(e) => handleStepChange(idx, 'instructions', e.target.value)}
              readOnly={readOnly}
              disabled={readOnly}
            />
            <div className="flex gap-6 pt-2">
              <label className="flex items-center gap-2 text-white/80">
                <Switch
                  checked={step.hasNote}
                  onCheckedChange={(v) => handleStepChange(idx, 'hasNote', v)}
                  disabled={readOnly}
                />
                Note
              </label>
              <label className="flex items-center gap-2 text-white/80">
                <Switch
                  checked={step.hasCheckbox}
                  onCheckedChange={(v) => handleStepChange(idx, 'hasCheckbox', v)}
                  disabled={readOnly}
                />
                Checkbox
              </label>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

export type { StepConfig };
export { DispositionWorkflowBuilder };
